import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { useCatalogGroups, usePricingMutations } from '@/hooks/usePricing'
import { SERVICE_TYPE_LABELS, type ServiceCatalogGroup } from '@/types/pricing'

export function GroupsTab() {
  const { data: groups, isLoading } = useCatalogGroups()
  const mutations = usePricingMutations()
  const [showDialog, setShowDialog] = useState(false)
  const [editingGroup, setEditingGroup] = useState<ServiceCatalogGroup | null>(null)

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72 mt-2" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Service Groups</CardTitle>
              <CardDescription>
                Organize catalog items into display groups. System groups are provided by default;
                add custom groups to match your operational categories.
              </CardDescription>
            </div>
            <Button onClick={() => { setEditingGroup(null); setShowDialog(true) }}>
              <Plus className="mr-2 h-4 w-4" />
              Add Group
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Allowed Types</TableHead>
                <TableHead>Sort Order</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups?.map((group) => (
                <TableRow key={group.id}>
                  <TableCell className="font-mono text-xs">{group.code}</TableCell>
                  <TableCell>{group.name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {group.allowedTypes?.map((t) => (
                        <Badge key={t} variant="outline" className="text-[10px]">
                          {SERVICE_TYPE_LABELS[t as keyof typeof SERVICE_TYPE_LABELS] ?? t}
                        </Badge>
                      )) ?? <span className="text-xs text-muted-foreground">Any</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{group.sortOrder}</TableCell>
                  <TableCell>
                    <Badge variant={group.isSystemGroup ? 'default' : 'outline'}>
                      {group.isSystemGroup ? 'System' : 'Custom'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {!group.isSystemGroup && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => { setEditingGroup(group); setShowDialog(true) }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive"
                          onClick={() => mutations.removeGroup.mutate(group.id)}
                          disabled={mutations.removeGroup.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {(!groups || groups.length === 0) && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No groups configured.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <GroupDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        group={editingGroup}
        onSubmit={(input) => {
          if (editingGroup) {
            mutations.updateGroup.mutate(
              { id: editingGroup.id, data: input },
              { onSuccess: () => setShowDialog(false) }
            )
          } else {
            mutations.createGroup.mutate(input, {
              onSuccess: () => setShowDialog(false),
            })
          }
        }}
        isPending={mutations.createGroup.isPending || mutations.updateGroup.isPending}
      />
    </>
  )
}

function GroupDialog({
  open,
  onOpenChange,
  group,
  onSubmit,
  isPending,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  group: ServiceCatalogGroup | null
  onSubmit: (input: {
    code: string
    name: string
    description?: string
    sortOrder?: number
    icon?: string
  }) => void
  isPending: boolean
}) {
  const [code, setCode] = useState(group?.code ?? '')
  const [name, setName] = useState(group?.name ?? '')
  const [sortOrder, setSortOrder] = useState(String(group?.sortOrder ?? 0))

  // Reset form when dialog opens with new group
useState(() => {
    setCode(group?.code ?? '')
    setName(group?.name ?? '')
    setSortOrder(String(group?.sortOrder ?? 0))
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      code: code.toLowerCase().replace(/\s+/g, '_'),
      name,
      sortOrder: Number(sortOrder) || 0,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{group ? 'Edit Group' : 'Add Custom Group'}</DialogTitle>
            <DialogDescription>
              {group ? `Edit "${group.name}".` : 'Create a group to organize catalog items.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="group-name">Name</Label>
              <Input
                id="group-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Cleaning & Detailing"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="group-code">Code</Label>
              <Input
                id="group-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g., cleaning_detailing"
                required
                className="font-mono"
                disabled={!!group}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="group-sort">Sort Order</Label>
              <Input
                id="group-sort"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !name || !code}>
              {isPending ? 'Saving...' : group ? 'Update Group' : 'Create Group'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
